import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Heart, Copy, Check, Share2, Send, ShieldAlert, ShieldCheck, ScrollText, MessageCircle,
  Twitter, Facebook, Linkedin, Mail, MessageSquare, Settings, Flame,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { RenderMarkdownInline, RenderMarkdownBlock } from '../lib/markdown';
import { SOCIAL } from '../lib/social';
import PageTitle from '../components/PageTitle';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  SOCIAL.twitter.enabled !== false && { label: SOCIAL.twitter.label, href: SOCIAL.twitter.url, Icon: Twitter },
  SOCIAL.facebook.enabled !== false && { label: SOCIAL.facebook.label, href: SOCIAL.facebook.url, Icon: Facebook },
  SOCIAL.linkedin.enabled !== false && { label: SOCIAL.linkedin.label, href: SOCIAL.linkedin.url, Icon: Linkedin },
].filter(Boolean);

const SectionCard = ({ icon: Icon, title, children, testId }) => (
  <motion.section
    id={testId}
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.45, ease: 'easeOut' }}
    className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8 scroll-mt-24"
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-md bg-[#DBEAFE] text-[#1E40AF] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <h2 className="text-xl font-bold text-[#020617] tracking-tight">{title}</h2>
    </div>
    <div className="text-[15px] leading-relaxed text-[#334155] space-y-3">
      {children}
    </div>
  </motion.section>
);

const About = () => {
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [referralUrl, setReferralUrl] = useState('');
  const [inviteStats, setInviteStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/page-content/about`)
      .then((r) => { if (!cancelled) setContent(r.data); })
      .catch(() => { if (!cancelled) setContent(null); });
    return () => { cancelled = true; };
  }, []);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/invite/stats`, { withCredentials: true });
      setInviteStats(r.data);
    } catch {
      setInviteStats(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [linkRes, statsRes] = await Promise.all([
          axios.get(`${API}/invite/link`, { withCredentials: true }),
          axios.get(`${API}/invite/stats`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setReferralUrl(linkRes.data.referral_url || '');
        setInviteStats(statsRes.data);
      } catch {
        if (!cancelled) {
          setReferralUrl('');
          setInviteStats(null);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]);

  const copyLink = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const SHARE_TEXT = 'Forge your financial future — give Forge Ledger a try.';
  const shareTargets = useMemo(() => {
    if (!referralUrl) return [];
    return [
      {
        key: 'twitter',
        label: 'Post on X',
        Icon: Twitter,
        bg: '#000000',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(referralUrl)}&via=${encodeURIComponent(SOCIAL.twitter.handle.replace(/^@/, ''))}`,
      },
      {
        key: 'facebook',
        label: 'Share on Facebook',
        Icon: Facebook,
        bg: '#1877F2',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`,
      },
      {
        key: 'linkedin',
        label: 'Share on LinkedIn',
        Icon: Linkedin,
        bg: '#0A66C2',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}`,
      },
      {
        key: 'whatsapp',
        label: 'Send via WhatsApp',
        Icon: MessageSquare,
        bg: '#25D366',
        url: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${referralUrl}`)}`,
      },
      {
        key: 'email',
        label: 'Email it',
        Icon: Mail,
        bg: '#475569',
        url: `mailto:?subject=${encodeURIComponent('Forge Ledger — thought you might find this useful')}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${referralUrl}`)}`,
      },
    ];
  }, [referralUrl]);

  const openShare = useCallback((target) => {
    if (target.key === 'email') {
      window.location.href = target.url;
      return;
    }
    window.open(target.url, '_blank', 'noopener,noreferrer,width=600,height=600');
  }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/invite`, {
        to_email: inviteEmail.trim(),
        message: inviteMessage.trim() || null,
      }, { withCredentials: true });
      toast.success(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteMessage('');
      fetchStats();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="about-page">
      <Seo
        title="About Forge Ledger — Why we built it"
        description="The story behind Forge Ledger — a UK-built personal finance tool for forecasting your balance, catching shortfalls early, and ditching the spreadsheet."
        path="/about"
      />
      <div className="p-6 sm:p-8 max-w-4xl mx-auto flex-1 w-full">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10"
      >
        <PageTitle
          title="About Forge Ledger"
          subtitle={content?.hero_subtitle || "A personal finance tool built for people who'd rather forecast than firefight."}
          testId="about-title"
        />
        {isStaffOrAbove(user) && (
          <Link
            to="/admin/edit/about"
            className="inline-flex items-center gap-1.5 mt-1 text-xs font-semibold text-[#0052FF] hover:underline"
            data-testid="about-edit-cta"
          >
            <Settings className="w-3.5 h-3.5" />
            Edit this page (staff only)
          </Link>
        )}
      </motion.div>

      <div className="space-y-6">
        {/* Mission */}
        <SectionCard icon={Heart} title={content?.mission_heading || 'Our mission'} testId="about-section-mission">
          <RenderMarkdownBlock text={(content?.mission_paragraphs || []).join('\n\n')} />
        </SectionCard>

        {/* Our Beginnings — origin story. Section hides itself if no story
            has been written yet, so the public page stays clean until the
            owner adds copy via /admin/edit/about. */}
        {(content?.origin_story || '').trim().length > 0 && (
          <SectionCard
            icon={Flame}
            title={content?.origin_heading || 'Our Beginnings'}
            testId="about-section-origin"
          >
            <RenderMarkdownBlock text={content.origin_story} />
          </SectionCard>
        )}

        {/* Security — customer-friendly version of the security audit */}
        <SectionCard
          icon={ShieldCheck}
          title={content?.security_heading || 'Your data, kept safe'}
          testId="about-section-security"
        >
          <p>
            <RenderMarkdownInline text={content?.security_intro || ''} />
          </p>
          <div className="grid gap-4 mt-2">
            {(content?.security_pillars || []).map((pillar, i) => (
              <div
                key={i}
                className="border-l-2 border-[#DBEAFE] pl-4"
                data-testid={`about-security-pillar-${i}`}
              >
                <h3 className="font-bold text-[#020617] text-[15px] mb-1">{pillar.heading}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">
                  <RenderMarkdownInline text={pillar.body} />
                </p>
              </div>
            ))}
          </div>
          {content?.security_footnote && (
            <p className="text-sm text-[#64748B] italic border-t border-[#E2E8F0] pt-4 mt-2">
              <RenderMarkdownInline text={content.security_footnote} />
            </p>
          )}
          <div className="border-t border-[#E2E8F0] pt-4 mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-[#64748B]">
              Manage which cookies we use. You can change this at any time.
            </p>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('forge:open-cookie-settings'))}
              className="text-xs font-medium text-[#0052FF] hover:underline"
              data-testid="about-cookie-settings-trigger"
            >
              Cookie preferences →
            </button>
          </div>
        </SectionCard>

        {/* Forecast disclaimer */}
        <SectionCard icon={ShieldAlert} title={content?.disclaimer_heading || 'Disclaimer on projected balances'} testId="about-section-disclaimer">
          <p className="italic text-[#475569]">
            <RenderMarkdownInline text={content?.disclaimer_intro || ''} />
          </p>
          <p className="text-sm text-[#64748B]">
            <RenderMarkdownInline text={content?.disclaimer_fineprint || ''} />
          </p>
        </SectionCard>

        {/* Invite friends/family */}
        <SectionCard icon={Share2} title={content?.invite_heading || 'Invite friends and family'} testId="about-section-invite">
          <p>
            <RenderMarkdownInline text={content?.invite_intro || ''} />
          </p>

          {inviteStats && inviteStats.total > 0 && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#DBEAFE] text-[#1E40AF] text-sm font-medium"
              data-testid="invite-stats-badge"
            >
              <Heart className="w-3.5 h-3.5" />
              You&apos;ve invited <span className="font-bold">{inviteStats.total}</span> {inviteStats.total === 1 ? 'person' : 'people'} — thanks for spreading the word.
            </div>
          )}

          {/* Share link */}
          <div className="mt-4">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#64748B] mb-2">Your referral link</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={referralUrl}
                readOnly
                placeholder="Loading…"
                className="font-mono text-sm bg-[#F8FAFC]"
                data-testid="invite-referral-input"
              />
              <Button
                onClick={copyLink}
                disabled={!referralUrl}
                className="bg-[#0052FF] text-white hover:bg-[#003ECC] whitespace-nowrap"
                data-testid="invite-copy-button"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>

            {/* One-click share bar — prefilled posts to X, Facebook, WhatsApp, Email */}
            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#64748B] mb-2">One-click share</div>
              <div className="flex flex-wrap gap-2" data-testid="invite-share-bar">
                {shareTargets.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => openShare(t)}
                    disabled={!referralUrl}
                    style={{ backgroundColor: t.bg }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    data-testid={`invite-share-${t.key}`}
                    aria-label={t.label}
                  >
                    <t.Icon className="w-4 h-4" />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Email invite */}
          <form onSubmit={sendInvite} className="mt-6 space-y-3" data-testid="invite-email-form">
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#64748B]">Or email an invite directly</div>
            <Input
              type="email"
              required
              placeholder="friend@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={sending}
              data-testid="invite-email-input"
            />
            <Textarea
              placeholder="Optional personal note (max 500 chars)"
              value={inviteMessage}
              maxLength={500}
              onChange={(e) => setInviteMessage(e.target.value)}
              disabled={sending}
              rows={3}
              data-testid="invite-message-input"
            />
            <Button
              type="submit"
              disabled={sending || !inviteEmail.trim()}
              className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
              data-testid="invite-send-button"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : 'Send invite'}
            </Button>
            <p className="text-xs text-[#94A3B8]"><RenderMarkdownInline text={content?.invite_footnote || "5 invites per hour, per account. We'll never spam your contact."} /></p>
          </form>
        </SectionCard>

        {/* Community / socials */}
        <SectionCard icon={MessageCircle} title={content?.community_heading || 'Join the community'} testId="about-section-community">
          <p>
            <RenderMarkdownInline text={content?.community_intro || ''} />
          </p>
          <div className="flex flex-wrap gap-3 mt-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#334155] hover:border-[#0052FF] hover:text-[#0052FF] transition-colors"
                data-testid={`about-social-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <s.Icon className="w-4 h-4" />
                {s.label}
              </a>
            ))}
          </div>
        </SectionCard>

        {/* Terms & Conditions */}
        <SectionCard icon={ScrollText} title={content?.terms_heading || 'Terms & Conditions'} testId="about-section-terms">
          <p className="text-sm text-[#64748B] italic">
            <RenderMarkdownInline text={content?.terms_intro || ''} />
          </p>

          {(content?.terms_sections || []).map((s, i) => (
            <React.Fragment key={`${s.heading}-${i}`}>
              <h3 className="font-semibold text-[#020617] text-base mt-3">{s.heading}</h3>
              <p className="text-sm">
                <RenderMarkdownInline text={s.body} />
              </p>
            </React.Fragment>
          ))}

          <p className="text-xs text-[#94A3B8] mt-4">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
          </p>
        </SectionCard>
      </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default About;
